import React, { useState, useEffect, useCallback } from "react";
import {
  Button, Dialog, Slide, Box, Chip, TextField, Typography,
  InputAdornment, Checkbox, Tooltip, IconButton, Menu, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Pagination
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import IndeterminateCheckBoxRoundedIcon from "@mui/icons-material/IndeterminateCheckBoxRounded";
import WithdrawForm from "./WithdrawForm";
import { fetchData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import { exportToCSV, filterRows } from "../../utils/tableUtils";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ROWS_PER_PAGE = 10;

const statusConfig = {
  Submit:        { color: "#4caf50", bg: "#e8f5e9" },
  Submitted:     { color: "#4caf50", bg: "#e8f5e9" },
  Approved:      { color: "#4caf50", bg: "#e8f5e9" },
  Cancel:        { color: "#f44336", bg: "#fce4ec" },
  Cancelled:     { color: "#f44336", bg: "#fce4ec" },
  Rejected:      { color: "#f44336", bg: "#fce4ec" },
  Edit:          { color: "#2196f3", bg: "#e3f2fd" },
  Pending:       { color: "#ff9800", bg: "#fff3e0" },
  "Under Review":{ color: "#ff9800", bg: "#fff3e0" },
};

const fallback = [
  { _id: "1", studentName: "Stella",    registrationNo: "001", grade: "Nilai05", parentName: "David Mark",   phoneNumber: "+212 555-0147", requestDate: "12.03.2026", status: "Submit",  fileUrl: "" },
  { _id: "2", studentName: "Stella",    registrationNo: "002", grade: "Nilai05", parentName: "John David",   phoneNumber: "+212 555-0147", requestDate: "12.03.2026", status: "Cancel",  fileUrl: "" },
  { _id: "3", studentName: "Julie",     registrationNo: "003", grade: "Nilai05", parentName: "Robert Klaus", phoneNumber: "+212 555-0147", requestDate: "12.03.2026", status: "Cancel",  fileUrl: "" },
  { _id: "4", studentName: "William",   registrationNo: "004", grade: "Nilai05", parentName: "James Gold",   phoneNumber: "+212 555-0147", requestDate: "12.03.2026", status: "Edit",    fileUrl: "" },
  { _id: "5", studentName: "Christina", registrationNo: "005", grade: "Nilai05", parentName: "David Mark",   phoneNumber: "+212 555-0147", requestDate: "12.03.2026", status: "Submit",  fileUrl: "" },
  { _id: "6", studentName: "Eloise",    registrationNo: "006", grade: "Nilai05", parentName: "John David",   phoneNumber: "+212 555-0147", requestDate: "12.03.2026", status: "Submit",  fileUrl: "" },
];

const exportColumns = [
  { label: "Student Name",    field: "studentName" },
  { label: "Reg No",          field: "registrationNo" },
  { label: "Grade",           field: "grade" },
  { label: "Parent Name",     field: "parentName" },
  { label: "Parent Ph.no",    field: "phoneNumber" },
  { label: "Date",            field: "requestDate" },
  { label: "Status",          field: "status" },
];

const TABLE_HEAD = [
  "Student Name", "Reg No", "Grade", "Parent Name",
  "Parent Ph.no", "Date", "Doc.Upload", "Status", ""
];

function Withdraw() {
  const [open, setOpen]                   = useState(false);
  const [rawData, setRawData]             = useState([]);
  const [selectedIds, setSelectedIds]     = useState(new Set());
  const [filterText, setFilterText]       = useState("");
  const [showFilter, setShowFilter]       = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [page, setPage]                   = useState(1);
  const [toast, setToast]                 = useState({ open: false, severity: "success", message: "" });
  const [menuAnchor, setMenuAnchor]       = useState(null);
  const [, setMenuRowId]                  = useState(null);

  const loadWithdrawals = useCallback(async () => {
    try {
      const response = await fetchData("student/withdrawl-enquirie/list");
      if (response?.data) {
        const data = Array.isArray(response.data) ? response.data : response.data.data || [];
        const mapped = data.map((r, i) => ({
          ...r,
          _id: String(r.id || r._id || i + 1),
          requestDate: r.requestDate || (r.createdAt ? new Date(r.createdAt).toLocaleDateString("en-GB").replace(/\//g, ".") : "—"),
          status: r.status || "Pending",
        }));
        setRawData(mapped.length > 0 ? mapped : fallback);
      } else {
        setRawData(fallback);
      }
    } catch (_) {
      setRawData(fallback);
    }
  }, []);

  useEffect(() => { loadWithdrawals(); }, [loadWithdrawals]);

  // ── Selection ──────────────────────────────────────────────
  const filteredRaw = filterRows(rawData, filterText);

  const allSelected  = filteredRaw.length > 0 && filteredRaw.every((r) => selectedIds.has(r._id));
  const someSelected = !allSelected && filteredRaw.some((r) => selectedIds.has(r._id));

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredRaw.map((r) => r._id)));
  };

  // ── Pagination ─────────────────────────────────────────────
  const totalPages   = Math.max(1, Math.ceil(filteredRaw.length / ROWS_PER_PAGE));
  const pagedRows    = filteredRaw.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  const handlePageChange = (_, value) => setPage(value);

  // ── Actions ────────────────────────────────────────────────
  const handleExport = () => exportToCSV(exportColumns, filteredRaw, "withdrawal_enquiries.csv");

  const handleDeleteConfirm = () => {
    setRawData([]);
    setSelectedIds(new Set());
    setDeleteConfirm(false);
    setToast({ open: true, severity: "success", message: "Records cleared from view" });
  };

  const handleOpenMenu = (e, id) => { setMenuAnchor(e.currentTarget); setMenuRowId(id); };
  const handleCloseMenu = () => { setMenuAnchor(null); setMenuRowId(null); };

  const handleDocView = (fileUrl) => { if (fileUrl) window.open(fileUrl, "_blank"); };

  // ── Styles ─────────────────────────────────────────────────
  const s = {
    container:   { textAlign: "left", padding: "1rem" },
    headerRow:   { display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 },
    addBtn:      { background: "#1E293B", color: "#fff", fontSize: "13px", textTransform: "none", padding: "8px 18px", borderRadius: "8px", "&:hover": { background: "#0f172a" } },
    outlineBtn:  { border: "1px solid #D5D7DA", borderRadius: "8px", color: "#374151", textTransform: "none", fontSize: "13px", padding: "7px 14px", gap: "6px" },
    badge:       { backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: "bold", fontSize: "12px", borderRadius: "20px", padding: "4px 12px", ml: 1.5 },
    tableWrap:   { borderRadius: "12px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", overflow: "hidden" },
    thead:       { backgroundColor: "#1E293B" },
    theadCell:   { color: "#fff", fontWeight: 600, fontSize: "13px", borderBottom: "none", py: 1.5, whiteSpace: "nowrap" },
    sortArrow:   { ml: 0.5, opacity: 0.7, fontSize: "12px" },
    tbodyRow:    { "&:hover": { backgroundColor: "#f8fafc" }, "&:last-child td": { border: 0 } },
    tdCell:      { fontSize: "13px", color: "#374151", py: 1.2 },
  };

  const StatusDot = ({ status }) => {
    const cfg = statusConfig[status] || { color: "#8F95B2", bg: "#f5f5f5" };
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: cfg.color, flexShrink: 0 }} />
        <Typography sx={{ fontSize: "13px", color: cfg.color, fontWeight: 500 }}>{status}</Typography>
      </Box>
    );
  };

  return (
    <div style={s.container}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />

      {/* Add Withdrawal Dialog */}
      <Dialog open={open} TransitionComponent={Transition} keepMounted sx={{ "& .MuiPaper-root": { borderRadius: "20px" } }}>
        <WithdrawForm handleClose={() => { setOpen(false); loadWithdrawals(); }} />
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)} maxWidth="xs" fullWidth>
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "16px", mb: 1 }}>Clear Records</Typography>
          <Typography sx={{ color: "#666", fontSize: "14px", mb: 3 }}>Remove all displayed records from this view?</Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={() => setDeleteConfirm(false)} sx={{ color: "#666", textTransform: "none", border: "1px solid #D5D7DA", borderRadius: "8px", px: 3 }}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} sx={{ backgroundColor: "#f44336", color: "#fff", textTransform: "none", borderRadius: "8px", px: 3 }}>Delete</Button>
          </Box>
        </Box>
      </Dialog>

      {/* Row-level actions menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleCloseMenu}
        PaperProps={{ sx: { borderRadius: "10px", boxShadow: "0 4px 20px rgba(0,0,0,0.12)", minWidth: 140 } }}>
        <MenuItem onClick={handleCloseMenu} sx={{ fontSize: "13px" }}>View Details</MenuItem>
        <MenuItem onClick={handleCloseMenu} sx={{ fontSize: "13px" }}>Edit</MenuItem>
        <MenuItem onClick={handleCloseMenu} sx={{ fontSize: "13px", color: "#f44336" }}>Delete</MenuItem>
      </Menu>

      {/* ── Page Header ── */}
      <p style={{ color: "rgba(0, 0, 0, 0.5)", fontWeight: "bold", fontSize: "14px", margin: 0 }}>
        Pages / Withdrawal Enquiries
      </p>

      <Box sx={s.headerRow}>
        {/* Left — title + badge + subtitle */}
        <Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ fontWeight: "bold", fontSize: "22px" }}>Withdrawal Enquiries</Typography>
            <Chip label={`${filteredRaw.length} Complaints`} sx={s.badge} />
          </Box>
          <Typography sx={{ color: "#8F95B2", fontSize: "13px", mt: 0.3 }}>
            Withdrawal list and their status
          </Typography>
        </Box>

        {/* Right — action buttons */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
          <Button startIcon={<DeleteOutlineIcon sx={{ fontSize: "16px" }} />} sx={s.outlineBtn}
            onClick={() => setDeleteConfirm(true)}>
            Delete
          </Button>
          <Button startIcon={<FilterListIcon sx={{ fontSize: "16px" }} />}
            sx={{ ...s.outlineBtn, ...(showFilter ? { backgroundColor: "#f0edff", color: "#244C77", borderColor: "#244C77" } : {}) }}
            onClick={() => setShowFilter((v) => !v)}>
            Filters
          </Button>
          <Button startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: "16px" }} />}
            sx={{ ...s.outlineBtn, border: "1.5px solid #244C77", color: "#244C77" }}
            onClick={handleExport}>
            Export
          </Button>
          <Button startIcon={<AddIcon sx={{ fontSize: "16px" }} />} sx={s.addBtn} onClick={() => setOpen(true)}>
            Add Withdrawal
          </Button>
        </Box>
      </Box>

      {/* Filter bar */}
      {showFilter && (
        <Box sx={{ mb: 2 }}>
          <TextField
            size="small" fullWidth placeholder="Search by name, grade, status..."
            value={filterText} onChange={(e) => { setFilterText(e.target.value); setPage(1); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#8F95B2", fontSize: "18px" }} /></InputAdornment> }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", "& fieldset": { borderColor: "#D5D7DA" } } }}
          />
        </Box>
      )}

      {/* ── Table ── */}
      <Paper elevation={0} sx={s.tableWrap}>
        <TableContainer>
          <Table>
            <TableHead sx={s.thead}>
              <TableRow>
                {/* Select-all checkbox */}
                <TableCell sx={{ ...s.theadCell, width: 48 }}>
                  <Tooltip title={allSelected ? "Deselect all" : "Select all"}>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={handleSelectAll}
                      icon={<CheckBoxOutlineBlankRoundedIcon sx={{ color: "#fff", fontSize: "20px" }} />}
                      checkedIcon={<CheckBoxRoundedIcon sx={{ color: "#fff", fontSize: "20px" }} />}
                      indeterminateIcon={<IndeterminateCheckBoxRoundedIcon sx={{ color: "#4caf50", fontSize: "20px" }} />}
                      sx={{ padding: "2px" }}
                    />
                  </Tooltip>
                </TableCell>
                {TABLE_HEAD.map((h) => (
                  <TableCell key={h} sx={s.theadCell}>
                    {h}{h && h !== "" && <span style={{ marginLeft: 4, opacity: 0.6, fontSize: "11px" }}>↓</span>}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {pagedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={TABLE_HEAD.length + 1} align="center" sx={{ py: 6, color: "#8F95B2" }}>
                    No records found
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.map((row) => (
                  <TableRow key={row._id} sx={s.tbodyRow}>
                    <TableCell sx={{ py: 0.8 }}>
                      <Checkbox
                        checked={selectedIds.has(row._id)}
                        onChange={() => handleToggleSelect(row._id)}
                        icon={<CheckBoxOutlineBlankRoundedIcon sx={{ color: "#D5D7DA" }} />}
                        checkedIcon={<CheckBoxRoundedIcon sx={{ color: "#4caf50" }} />}
                        sx={{ padding: "4px" }}
                      />
                    </TableCell>
                    <TableCell sx={s.tdCell}>{row.studentName || "—"}</TableCell>
                    <TableCell sx={s.tdCell}>{row.registrationNo || "—"}</TableCell>
                    <TableCell sx={s.tdCell}>{row.grade || "—"}</TableCell>
                    <TableCell sx={s.tdCell}>{row.parentName || "—"}</TableCell>
                    <TableCell sx={s.tdCell}>{row.phoneNumber || "—"}</TableCell>
                    <TableCell sx={s.tdCell}>{row.requestDate || "—"}</TableCell>

                    {/* Doc Upload column */}
                    <TableCell sx={{ py: 0.8 }}>
                      <Tooltip title={row.fileUrl ? "View document" : "No document uploaded"}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={!row.fileUrl}
                            onClick={() => handleDocView(row.fileUrl)}
                            sx={{
                              backgroundColor: row.fileUrl ? "#fde8e8" : "#f5f5f5",
                              borderRadius: "6px",
                              padding: "6px 10px",
                              "&:hover": { backgroundColor: row.fileUrl ? "#fbc5c5" : "#f5f5f5" },
                            }}
                          >
                            <PictureAsPdfIcon sx={{ fontSize: "18px", color: row.fileUrl ? "#f44336" : "#ccc" }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>

                    {/* Status */}
                    <TableCell sx={s.tdCell}>
                      <StatusDot status={row.status} />
                    </TableCell>

                    {/* Three-dot menu */}
                    <TableCell sx={{ py: 0.8 }} align="right">
                      <IconButton size="small" onClick={(e) => handleOpenMenu(e, row._id)}
                        sx={{ color: "#9CA3AF" }}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2, borderTop: "1px solid #F3F4F6" }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              shape="rounded"
              sx={{
                "& .MuiPaginationItem-root": { fontSize: "13px", color: "#374151" },
                "& .Mui-selected": { backgroundColor: "#1E293B !important", color: "#fff !important" },
              }}
            />
          </Box>
        )}
      </Paper>
    </div>
  );
}

export default Withdraw;
